import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, type SelectPanelProps, VisuallyHidden} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {RepositoryPickerOwner} from '../utils/get-repository-search-query'
import {
  fetchAccountIssueOrganizations,
  fetchSearchRepositories,
  fetchTopRepositories,
} from './fetch-org-repository-picker-items'
import {
  organizationItemKey,
  type OrgRepositoryPickerItem,
  type OrgRepositoryPickerOrganization as OrgRepositoryPickerOrganizationType,
  type OrgRepositoryPickerRepository as OrgRepositoryPickerRepositoryType,
  type OrgRepositoryPickerSelection as OrgRepositoryPickerSelectionType,
  repositoryItemKey,
  selectionFromItem,
} from './org-repository-picker-types'
import {
  destinationAccessibleName,
  OrganizationDestinationVisual,
  RepositoryDestinationVisual,
} from './org-repository-picker-visuals'
import styles from './OrgRepositoryPicker.module.css'

export type OrgRepositoryPickerOrganization = OrgRepositoryPickerOrganizationType
export type OrgRepositoryPickerRepository = OrgRepositoryPickerRepositoryType
export type OrgRepositoryPickerSelection = OrgRepositoryPickerSelectionType

const PLACEHOLDER = 'Select organization or repository'
const SEARCH_PLACEHOLDER = 'Search'

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

function anchorLabel(selection: OrgRepositoryPickerSelection | undefined): string {
  if (!selection) return PLACEHOLDER
  return selection.kind === 'org' ? selection.organization.name : selection.repository.nameWithOwner
}

export type OrgRepositoryPickerProps = {
  initialSelection?: OrgRepositoryPickerSelection
  onSelect: (selection: OrgRepositoryPickerSelection | undefined) => void
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  /**
   * Restricts results to a single owner (e.g. the org planning tab). Orgs are filtered to this
   * login and repos are fetched/searched within it. Omit for the broader cross-owner flow.
   */
  scope?: RepositoryPickerOwner
}

export function OrgRepositoryPicker({initialSelection, onSelect, anchorElement, scope}: OrgRepositoryPickerProps) {
  const {addToast} = useToastContext()

  const [selection, setSelection] = useState<OrgRepositoryPickerSelection | undefined>(initialSelection)
  const [organizations, setOrganizations] = useState<OrgRepositoryPickerOrganization[]>([])
  const [topRepositories, setTopRepositories] = useState<OrgRepositoryPickerRepository[]>([])
  const [searchResults, setSearchResults] = useState<OrgRepositoryPickerRepository[] | undefined>(undefined)
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(true)

  const filterRef = useRef(filter)
  const searchAbortRef = useRef<AbortController | undefined>(undefined)
  const latestSearchRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        // A user scope has no orgs to show; only fetch (and server-scope) orgs for an org scope.
        const orgScopeLogin = scope?.type === 'organization' ? scope.login : undefined
        const orgsPromise = scope?.type === 'user' ? Promise.resolve([]) : fetchAccountIssueOrganizations(orgScopeLogin)
        const [orgs, repos] = await Promise.all([orgsPromise, fetchTopRepositories(scope)])
        if (cancelled) return
        setOrganizations(orgs)
        setTopRepositories(repos)
      } catch {
        if (cancelled) return
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({type: 'error', message: ERRORS.couldNotSearchRepositories})
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [addToast, scope])

  useEffect(() => {
    return () => {
      latestSearchRef.current += 1
      searchAbortRef.current?.abort()
    }
  }, [])

  const runSearch = useCallback(
    (searchQuery: string) => {
      searchAbortRef.current?.abort()
      const trimmed = searchQuery.trim()
      if (trimmed === '') {
        latestSearchRef.current += 1
        setSearchResults(undefined)
        setLoading(false)
        return
      }

      const requestId = latestSearchRef.current + 1
      latestSearchRef.current = requestId
      const controller = new AbortController()
      searchAbortRef.current = controller
      setLoading(true)

      void (async () => {
        try {
          const repos = await fetchSearchRepositories(trimmed, scope)
          if (requestId === latestSearchRef.current && trimmed === filterRef.current.trim()) {
            setSearchResults(repos)
            setLoading(false)
          }
        } catch (error) {
          if (requestId !== latestSearchRef.current) return
          setLoading(false)
          if (isAbortError(error)) return
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({type: 'error', message: ERRORS.couldNotSearchRepositories})
        } finally {
          if (searchAbortRef.current === controller) searchAbortRef.current = undefined
        }
      })()
    },
    [addToast, scope],
  )

  const debouncedSearch = useDebounce(runSearch, VALUES.pickerDebounceTime)

  const filterItems = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      if (filter === trimmed) return
      setFilter(trimmed)
      filterRef.current = trimmed
      debouncedSearch(trimmed)
    },
    [filter, debouncedSearch],
  )

  // Orgs are filtered client-side; repos come from the search endpoint, or top repos when not searching.
  const items = useMemo<OrgRepositoryPickerItem[]>(() => {
    const trimmed = filter.trim().toLowerCase()
    // When scoped to an owner, only that org may appear (the server already scopes org results, so
    // this is a case-insensitive safeguard rather than the source of truth).
    const scopedOrgs = scope
      ? organizations.filter(org => org.login.toLowerCase() === scope.login.toLowerCase())
      : organizations
    const matchingOrgs = trimmed
      ? scopedOrgs.filter(org => org.login.toLowerCase().includes(trimmed) || org.name.toLowerCase().includes(trimmed))
      : scopedOrgs

    // Selecting an org already resolves to its designated Account Issues repository, so drop that
    // source repo from the repo results to avoid offering it as a separate creation target. Keep the
    // initially selected repo though: filtering it out drops it from ItemPicker's map, which clears
    // the destination when the panel closes without a new choice.
    const sourceRepoKeys = new Set(
      organizations.map(org => `${org.repositoryOwner}/${org.repositoryName}`.toLowerCase()),
    )
    const initialRepoKey =
      initialSelection?.kind === 'repo' ? initialSelection.repository.nameWithOwner.toLowerCase() : undefined
    const repos = (searchResults ?? topRepositories).filter(repo => {
      const key = repo.nameWithOwner.toLowerCase()
      return key === initialRepoKey || !sourceRepoKeys.has(key)
    })

    const orgItems: OrgRepositoryPickerItem[] = matchingOrgs.map(org => ({
      kind: 'org',
      key: organizationItemKey(org),
      organization: org,
    }))
    const repoItems: OrgRepositoryPickerItem[] = repos.map(repo => ({
      kind: 'repo',
      key: repositoryItemKey(repo),
      repository: repo,
    }))

    return [...orgItems, ...repoItems]
  }, [filter, organizations, searchResults, topRepositories, scope, initialSelection])

  const getItemKey = useCallback((item: OrgRepositoryPickerItem) => item.key, [])

  const convertToItemProps = useCallback(
    (item: OrgRepositoryPickerItem): ExtendedItemProps<OrgRepositoryPickerItem> => {
      if (item.kind === 'org') {
        const org = item.organization
        return {
          id: item.key,
          text: org.name,
          source: item,
          leadingVisual: () => <OrganizationDestinationVisual organization={org} />,
          // Type-qualifies the option name to match the selected value; the avatar is decorative.
          trailingVisual: <VisuallyHidden>organization</VisuallyHidden>,
          className: styles.Item,
        }
      }

      const repo = item.repository
      return {
        id: item.key,
        text: repo.nameWithOwner,
        source: item,
        leadingVisual: () => <RepositoryDestinationVisual repository={repo} />,
        // Type-qualifies the option name and announces visibility; the repo/lock icon is decorative.
        trailingVisual: <VisuallyHidden>{repo.isPrivate ? 'private repository' : 'repository'}</VisuallyHidden>,
        className: styles.Item,
      }
    },
    [],
  )

  const initialSelectedItems = useMemo<OrgRepositoryPickerItem[]>(() => {
    if (!initialSelection) return []
    return initialSelection.kind === 'org'
      ? [
          {
            kind: 'org',
            key: organizationItemKey(initialSelection.organization),
            organization: initialSelection.organization,
          },
        ]
      : [{kind: 'repo', key: repositoryItemKey(initialSelection.repository), repository: initialSelection.repository}]
  }, [initialSelection])

  const pickerRef = useRef<HTMLButtonElement>(null)

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) return anchorElement(anchorProps)

      const label = anchorLabel(selection)
      return (
        <Button
          leadingVisual={
            selection
              ? () =>
                  selection.kind === 'org' ? (
                    <OrganizationDestinationVisual organization={selection.organization} />
                  ) : (
                    <RepositoryDestinationVisual repository={selection.repository} />
                  )
              : undefined
          }
          trailingVisual={TriangleDownIcon}
          aria-label={selection ? destinationAccessibleName(selection) : PLACEHOLDER}
          {...anchorProps}
          ref={pickerRef}
        >
          <span>{label}</span>
        </Button>
      )
    },
    [anchorElement, selection],
  )

  const onSelectionChange = useCallback(
    ([firstItem]: OrgRepositoryPickerItem[]) => {
      const nextSelection = firstItem ? selectionFromItem(firstItem) : undefined
      setSelection(nextSelection)
      onSelect(nextSelection)
    },
    [onSelect],
  )

  const showNoMatchMessage = !loading && items.length === 0
  const message = useMemo<SelectPanelProps['message'] | undefined>(
    () =>
      showNoMatchMessage
        ? {
            title: 'No organizations or repositories match',
            body: 'Try searching with a different query for results.',
            variant: 'empty',
          }
        : undefined,
    [showNoMatchMessage],
  )

  return (
    <ItemPicker
      items={items}
      initialSelectedItems={initialSelectedItems}
      filterItems={filterItems}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={SEARCH_PLACEHOLDER}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      loading={loading}
      renderAnchor={renderAnchor}
      selectPanelRef={pickerRef}
      resultListAriaLabel="Organization and repository results"
      height="medium"
      width="medium"
      // Visually hide the SelectPanel's heading while keeping it as the dialog's accessible name.
      title={<VisuallyHidden>{PLACEHOLDER}</VisuallyHidden>}
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={message}
    />
  )
}
